import { useCallback, useEffect, useRef, useState } from 'react'
import type { FaqEntry } from '@netwatchai/chat-core'

type Headers = Record<string, string> | undefined

interface UseFaqOptions {
  apiEndpoint: string
  headers?: Record<string, string>
  enabled: boolean
}

/**
 * The user's saved questions — the same `/chat/faq` store the dashboard edits.
 *
 * The widget reaches it with its `X-Netwatch-Client-Id` + `X-Netwatch-Username`
 * headers (the backend's `FaqIdentityDep` accepts that pair alongside the SPA's
 * JWT; see docs/faq.md). The first GET for a user seeds the built-ins
 * server-side, so a loaded list is only empty if they deleted everything.
 *
 * No react-query here — one list, four calls: every mutation refetches, which
 * keeps the star cap and the seeding server-authoritative.
 */
export function useFaq({ apiEndpoint, headers, enabled }: UseFaqOptions) {
  // null = not loaded yet (or the load failed — `error` tells them apart).
  const [entries, setEntries] = useState<FaqEntry[] | null>(null)
  const [starCap, setStarCap] = useState(6)
  const [loadFailed, setLoadFailed] = useState(false)
  const [mutating, setMutating] = useState(false)
  // Last mutation failure — the backend writes the user-facing wording
  // ("that question is already saved", "star limit (6) reached…").
  const [error, setError] = useState<string | null>(null)
  const loadedRef = useRef(false)

  const headersRef = useRef<Headers>(headers)
  headersRef.current = headers

  const call = useCallback(
    async (path: string, init?: RequestInit) => {
      const res = await fetch(`${apiEndpoint}${path}`, {
        ...init,
        headers: { ...headersRef.current, ...(init?.body ? { 'Content-Type': 'application/json' } : {}) },
      })
      if (!res.ok) {
        // FastAPI puts the human sentence in `detail`.
        let detail = `HTTP ${res.status}`
        try {
          const body = await res.json()
          if (body?.detail) detail = String(body.detail)
        } catch { /* non-JSON error body — keep the status line */ }
        throw new Error(detail)
      }
      return res.status === 204 ? null : await res.json()
    },
    [apiEndpoint],
  )

  const refresh = useCallback(async () => {
    setLoadFailed(false)
    try {
      const data = await call('/chat/faq')
      setEntries(data.items || [])
      if (typeof data.star_cap === 'number') setStarCap(data.star_cap)
    } catch {
      setEntries(null)
      setLoadFailed(true)
    }
  }, [call])

  useEffect(() => {
    if (!enabled || loadedRef.current) return
    loadedRef.current = true
    refresh()
  }, [enabled, refresh])

  // Every mutation ends in a refetch: the server owns seeding, the star cap and
  // starred_at ordering, so re-reading is cheaper than mirroring those rules.
  const mutate = useCallback(
    async (run: () => Promise<unknown>, onDone?: () => void) => {
      setMutating(true)
      setError(null)
      try {
        await run()
        onDone?.()
        await refresh()
      } catch (err) {
        setError((err as Error).message || 'Something went wrong')
      } finally {
        setMutating(false)
      }
    },
    [refresh],
  )

  const add = useCallback(
    (question: string, category: string, onDone?: () => void) =>
      mutate(
        () => call('/chat/faq', { method: 'POST', body: JSON.stringify({ question, category }) }),
        onDone,
      ),
    [call, mutate],
  )

  const star = useCallback(
    (entry: FaqEntry) =>
      mutate(() =>
        call(`/chat/faq/${entry.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ starred: !entry.starred }),
        }),
      ),
    [call, mutate],
  )

  const remove = useCallback(
    (entry: FaqEntry) => mutate(() => call(`/chat/faq/${entry.id}`, { method: 'DELETE' })),
    [call, mutate],
  )

  return { entries, starCap, loadFailed, mutating, error, refresh, add, star, remove }
}
